/* Speech synthesis helpers: sentence-chunked TTS with pause/resume/progress, plus audio-file playback. */

export const speechBus = new EventTarget();
let activeCount = 0;
const setActive = (delta) => {
  activeCount = Math.max(0, activeCount + delta);
  speechBus.dispatchEvent(new CustomEvent(activeCount > 0 ? "start" : "end"));
};
export const isSiteSpeaking = () => activeCount > 0;

export const ttsSupported = () => typeof window !== "undefined" && "speechSynthesis" in window;

let voicesCache = [];
const loadVoices = () => {
  if (!ttsSupported()) return [];
  voicesCache = window.speechSynthesis.getVoices();
  return voicesCache;
};
if (ttsSupported()) {
  loadVoices();
  window.speechSynthesis.onvoiceschanged = loadVoices;
}

export const pickVoice = (lang) => {
  const voices = voicesCache.length ? voicesCache : loadVoices();
  const base = lang.split("-")[0].toLowerCase();
  return (
    voices.find((v) => v.lang.toLowerCase() === lang.toLowerCase()) ||
    voices.find((v) => v.lang.toLowerCase().startsWith(base)) ||
    null
  );
};
export const hasVoiceFor = (lang) => !!pickVoice(lang);

export const splitSentences = (text) =>
  text
    .replace(/\s+/g, " ")
    .match(/[^.!?।]+[.!?।]+["')\]]*|[^.!?।]+$/g)
    ?.map((s) => s.trim())
    .filter(Boolean) || [text];

/**
 * Speak text sentence by sentence. Returns a controller.
 * opts: { lang, rate, onSentence(i, total), onProgress(0..1), onEnd(), onError() }
 */
export const speak = (text, opts = {}) => {
  const { lang = "en-IN", onSentence, onProgress, onEnd, onError } = opts;
  let rate = opts.rate || 1;
  const sentences = Array.isArray(text) ? text : splitSentences(text);
  const totalChars = sentences.reduce((n, s) => n + s.length, 0) || 1;
  let i = 0;
  let doneChars = 0;
  let stopped = false;
  let utter = null;

  if (!ttsSupported()) {
    onError?.("unsupported");
    return { pause() {}, resume() {}, stop() {}, setRate() {} };
  }
  window.speechSynthesis.cancel();
  setActive(1);

  const finish = () => {
    if (stopped) return;
    stopped = true;
    setActive(-1);
    onProgress?.(1);
    onEnd?.();
  };

  const next = () => {
    if (stopped) return;
    if (i >= sentences.length) return finish();
    const s = sentences[i];
    utter = new SpeechSynthesisUtterance(s);
    utter.lang = lang;
    utter.rate = rate;
    const v = pickVoice(lang);
    if (v) utter.voice = v;
    onSentence?.(i, sentences.length);
    utter.onboundary = (e) => {
      if (e.name === "word") onProgress?.(Math.min(1, (doneChars + e.charIndex) / totalChars));
    };
    utter.onend = () => {
      doneChars += s.length;
      onProgress?.(Math.min(1, doneChars / totalChars));
      i += 1;
      next();
    };
    utter.onerror = (e) => {
      if (e.error === "interrupted" || e.error === "canceled") return;
      stopped = true;
      setActive(-1);
      onError?.(e.error);
    };
    window.speechSynthesis.speak(utter);
  };
  next();

  return {
    pause: () => window.speechSynthesis.pause(),
    resume: () => window.speechSynthesis.resume(),
    stop: () => {
      if (stopped) return;
      stopped = true;
      window.speechSynthesis.cancel();
      setActive(-1);
      onEnd?.();
    },
    setRate: (r) => {
      rate = r;
      // restart current sentence at new rate
      if (!stopped && utter) {
        window.speechSynthesis.cancel();
        next();
      }
    },
  };
};

export const stopAllSpeech = () => {
  if (ttsSupported()) window.speechSynthesis.cancel();
  if (activeCount > 0) {
    activeCount = 0;
    speechBus.dispatchEvent(new CustomEvent("end"));
  }
};

/** Play an audio file; resolves true when played, false when missing/failed (caller falls back). */
export const playAudioFile = (url, { onEnd, onProgress } = {}) =>
  new Promise((resolve) => {
    if (!url) return resolve(false);
    const audio = new Audio(url);
    let started = false;
    audio.onerror = () => resolve(false);
    audio.ontimeupdate = () => audio.duration && onProgress?.(audio.currentTime / audio.duration);
    audio.onended = () => {
      setActive(-1);
      onEnd?.();
    };
    audio
      .play()
      .then(() => {
        started = true;
        setActive(1);
        resolve(true);
      })
      .catch(() => !started && resolve(false));
  });
